import {
  CustomerSearchDto,
  CustomerDetailDto,
  CustomerAddressDto,
  DirectOrderItemDto,
  ItemLocationDto,
  CreateDirectOrderRequest,
  CompleteDirectOrderRequest,
  DirectOrderWithPListResult,
  DirectOrderResult
} from '../types/directOrder';
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

function getCompanyNo(): string {
  const userData = AuthService.getUserDataFromToken();
  if (!userData?.companyNo) {
    throw new Error('Ingen brukerdata tilgjengelig');
  }
  return userData.companyNo;
}

function getBaseUrl(): string {
  return `${API_BASE_URL}/${getCompanyNo()}/directorder`;
}

class DirectOrderService {

  async searchCustomers(query: string): Promise<CustomerSearchDto[]> {
    const url = `${getBaseUrl()}/customers/search?q=${encodeURIComponent(query)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kundesøk feilet (${response.status})`);
    }

    return response.json();
  }

  async getCustomer(customerNo: string): Promise<CustomerDetailDto> {
    const url = `${getBaseUrl()}/customers/${encodeURIComponent(customerNo)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Kunde ${customerNo} ikke funnet`);
      }
      throw new Error(`Kunne ikke hente kunde (${response.status})`);
    }

    return response.json();
  }

  async getCustomerAddresses(customerNo: string): Promise<CustomerAddressDto[]> {
    const url = `${getBaseUrl()}/customers/${encodeURIComponent(customerNo)}/addresses`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke hente adresser (${response.status})`);
    }

    return response.json();
  }

  async searchItems(query: string): Promise<DirectOrderItemDto[]> {
    const url = `${getBaseUrl()}/items/search?q=${encodeURIComponent(query)}`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Varesøk feilet (${response.status})`);
    }

    return response.json();
  }

  async getItemLocations(itemNo: string): Promise<ItemLocationDto[]> {
    const url = `${getBaseUrl()}/items/${encodeURIComponent(itemNo)}/locations`;
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      throw new Error(`Kunne ikke hente lokasjoner (${response.status})`);
    }

    return response.json();
  }

  async createOrderAndPick(request: CreateDirectOrderRequest): Promise<DirectOrderWithPListResult> {
    const url = `${getBaseUrl()}/create-and-pick`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke opprette ordre og plukkliste'));
    }

    return response.json();
  }

  async completeDirectOrder(request: CompleteDirectOrderRequest): Promise<DirectOrderResult> {
    const url = `${getBaseUrl()}/complete`;
    const response = await AuthService.authenticatedPost(url, request);

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'Kunne ikke fullføre ordre'));
    }

    return response.json();
  }
}

export const directOrderService = new DirectOrderService();
